import { createApp, defineAsyncComponent, ref, watch } from "vue";
import { Loader } from "@sazka/design-system/src/loader/loader";
import { showAlert } from "@sazka/design-system/src/modal-alert/mount-alert";
import { getNgTimeDuration, ngFrameworkLoaded } from "@sazka/ng/ng-integration";
import { useNgSession } from "@sazka/ng/ng-session";
import { confirmAge } from "@sazka/shared-components/age-confirmation/age-confirmation";
import { documentReady } from "@sazka/utils/document-ready";
import { eventBus, GlobalEvents } from "@sazka/utils/global-events";
import { pinia } from "@sazka/utils/global-pinia";
import { httpClient } from "@sazka/utils/http-clients/http-client";
import { ResponsiveUtils } from "@sazka/utils/responsive-utils";
import { analyticsCasinoGameDetail, analyticsCasinoToggleMoneyMode } from "./ng-casino-analytics";
import { useNgCasinoGameList } from "./ng-casino-game-list";
import { useNgCasinoUtils } from "./ng-casino-utils";
import { useNgCasinoFunparkStore } from "./use-ng-casino-funpark-store";
import { useNgCasinoState } from "./use-ng-casino-state";
import { CasinoGameLoadParams } from "../model/casino-game-load-params";
import { OnlineGameDetail } from "../model/online-game-detail";

const { isLoggedIn } = useNgSession();

let container: HTMLDivElement = null;

const {
    currentGameId,
    playForMoney,
    transparentGame,
    sessionStart,
    netLoss,
    currentGameServerData,
    ngGameDefinition,
    isLiveDealerGameOpen,
} = useNgCasinoState();

/** ID of the game that was successfully launched by NG */
const successfullyLaunchedGameId = ref(0);

let transparentGameMountModule: typeof import("./mount-casino-transparent-game-layer") = null;

const casinoGameMobileMode = ResponsiveUtils.isMobile();

let gameLoadPromiseResolve: (data?: any) => void = null;

const { fetchNgGameDefinition, gameListReady } = useNgCasinoGameList();

const showGameDetail = async () => {
    if (!container) {
        const casinoGameLayer = defineAsyncComponent(
            () => import(/* webpackPrefetch: true */ "../components/casino-game-layer.vue")
        );

        container = document.createElement("div");
        container.id = "casino-game-layer";
        document.body.append(container);
        const component = createApp(casinoGameLayer);
        component.mount(container);
    }
};

const showTransparentGameDetail = async () => {
    transparentGameMountModule ??= await import("./mount-casino-transparent-game-layer");
    transparentGameMountModule.launchCasinoTransparentGameLayer(currentGameId.value.toString());
};

/**
 * Close game request to NG
 */
const closeGame = () => {
    // handle case when NG doesn't start loading of the game, because of widget or error (CDD verification for example)
    if (!successfullyLaunchedGameId.value) {
        resetCasinoState();
    }
    successfullyLaunchedGameId.value = 0;

    if (currentGameId.value > 0) {
        ng_fw.closeGame();
    }
};

const loadGameFromMoneyModeToggle = () => {
    loadGame({
        gameId: currentGameId.value,
        moneyMode: playForMoney.value,
        loadSameGame: true,
    });
    analyticsCasinoToggleMoneyMode(playForMoney.value);
};

/**
 * Toggles money mode
 */
const toggleMoneyMode = () => {
    playForMoney.value = !playForMoney.value;

    if (playForMoney.value && !isLoggedIn.value) {
        ng_fw.showLogin();

        const stopWatch = watch(
            isLoggedIn,
            (val) => {
                if (val) {
                    loadGameFromMoneyModeToggle();
                }
            },
            { once: true }
        );

        eventBus.on(GlobalEvents.WebLoginAborted, () => stopWatch());
    } else {
        loadGameFromMoneyModeToggle();
    }
};

/**
 * NG confirms that game started loading,
 * we can show iframe
 * @param gameId Game ID of currently started game, supplied by NG
 * @param playMode Play mode supplied by NG
 */
const ngGameReady = (gameId: number, playMode: "M" | "D") => {
    if (currentGameId.value == gameId && playForMoney.value === (playMode === "M")) {
        gameLoadPromiseResolve();
        gameLoadPromiseResolve = null;
    } else {
        loadGameInitiatedByNg(gameId, playMode === "M");
    }
    currentGameId.value = gameId;
    playForMoney.value = playMode === "M";
    successfullyLaunchedGameId.value = gameId;
};

const ngLoadGame = (gameId: number, playForMoney: boolean) => {
    return new Promise<void>((res) => {
        gameLoadPromiseResolve = res;
        ng_fw.loadGame({ gameId, playMode: playForMoney ? "M" : "D" });
    });
};

async function loadTransparentGame(gameLoadParams: CasinoGameLoadParams) {
    transparentGame.value = true;
    playForMoney.value = true; // can be played in money mode only (may change for future transparent games)

    if (currentGameServerData.value?.gameId !== gameLoadParams.gameId) {
        const gameData = await loadServerData(gameLoadParams.gameId);
        currentGameServerData.value = gameData;
    }

    currentGameId.value = currentGameServerData.value.gameId;

    return ngLoadGame(currentGameId.value, playForMoney.value);
}

function resetCasinoState() {
    currentGameId.value = 0;
    currentGameServerData.value = null;
}

/**
 * Initiates game load process
 * @param gameLoadParams game load params
 */
async function loadGame(
    gameLoadParams: CasinoGameLoadParams,
    manufacturer = null,
    placement = null,
    position = 0,
    withAbTest = true
) {
    if (
        gameLoadParams == null ||
        isNaN(gameLoadParams.gameId) ||
        (gameLoadParams.gameId === currentGameId.value && !gameLoadParams.loadSameGame)
    ) {
        return;
    }

    if (!environment.isInAppView) {
        try {
            await confirmAge();
        } catch {
            return;
        }
    }

    Loader.showNow();

    try {
        const gameData = await loadServerData(gameLoadParams.gameId, withAbTest);

        const mobileId = gameData.gameMobileId || gameData.gameId;
        const desktopId = gameData.gameId;
        const gameId = casinoGameMobileMode ? mobileId : desktopId;

        await ngFrameworkLoaded();

        ngGameDefinition.value = await fetchNgGameDefinition(gameId);
        if (!ngGameDefinition.value) {
            Loader.hide();
            gameNotFoundAlert();
            return;
        }

        currentGameServerData.value = gameData;
        transparentGame.value = gameData?.transparent ?? false;
        currentGameId.value = gameId; // needs to be set after transparentGame, because of watch trigger in casinoGameLayer

        playForMoney.value = gameLoadParams.moneyMode ?? isLoggedIn.value;
        // check if game is available in demo mode
        if (playForMoney.value === false && !ngGameDefinition.value.playModes.includes("Demo")) {
            playForMoney.value = true;
        }

        analyticsCasinoGameDetail(
            currentGameServerData.value?.type,
            currentGameServerData.value?.codeName,
            playForMoney.value,
            currentGameId.value,
            manufacturer,
            placement,
            position,
            ngGameDefinition.value
        );
        Loader.hide();

        if (transparentGame.value) {
            showTransparentGameDetail();
        } else {
            await showGameDetail();
            await ngLoadGame(currentGameId.value, playForMoney.value);
        }

        useNgCasinoUtils().handleFullscreen();
    } catch (e) {
        console.error("Error loading game:", e);

        gameLoadAlert();
        Loader.hide();
    }
}

// game can be initiated from NG widget
async function loadGameInitiatedByNg(gameId: number, moneyMode: boolean) {
    playForMoney.value = moneyMode;
    currentGameId.value = gameId;
    Loader.showNow();

    try {
        const gameData = await loadServerData(currentGameId.value, false);
        currentGameServerData.value = gameData;

        transparentGame.value = currentGameServerData.value?.transparent ?? false;

        // TODO: switch to "force" money mode for transparent games?

        if (transparentGame.value) {
            showTransparentGameDetail();
        } else {
            await showGameDetail();
        }

        ngGameDefinition.value = await fetchNgGameDefinition(currentGameId.value);
        if (!ngGameDefinition.value) {
            Loader.hide();
            gameNotFoundAlert();
            return;
        }

        analyticsCasinoGameDetail(
            currentGameServerData.value?.type,
            currentGameServerData.value?.codeName,
            playForMoney.value,
            currentGameId.value,
            null,
            null,
            0
        );
        Loader.hide();
    } catch (e) {
        gameLoadAlert();
        Loader.hide();
    }
}

function gameLoadAlert() {
    resetCasinoState();

    showAlert({
        title: "Nastal problém při načítání hry",
        subText: "Zkuste prosím obnovit stránku a hru znovu spustit.",
        btnConfirmClickHandler: () => {
            location.reload();
        },
        btnConfirmText: "Obnovit stránku",
        btnCloseText: "Zavřít",
    });
}

function gameNotFoundAlert() {
    resetCasinoState();

    showAlert({
        title: "Připravujeme pro vás novou hru",
        subText:
            "Omlouváme se, ale hru není možné v tuto chvíli spustit. Připravujeme pro vás novou emisi. Mezitím vyzkoušejte další hry z naší nabídky.",
        btnCloseText: "Zavřít",
    });
}

/**
 * Load game data from Kentico
 * @param gameId Game to load server data
 */
async function loadServerData(gameId: number, withAbTest = true): Promise<OnlineGameDetail> {
    return await new Promise(async (res, rej) => {
        // is same game - for example on money mode change
        if (currentGameServerData.value?.gameId === gameId && !withAbTest) {
            res(currentGameServerData.value);
            return;
        }

        try {
            const response = await httpClient.get<OnlineGameDetail>(
                `/api/casino/game/${gameId}?withAbTest=${withAbTest}`
            );
            res(response.data);
        } catch (err) {
            rej(err);
        }
    });
}

function ngSessionUpdate(totalNetLoss: string, playingSessionStart: string, ngServerTime: string) {
    netLoss.value = Number(totalNetLoss) * -1;

    if (playingSessionStart) {
        sessionStart.value = getNgTimeDuration(playingSessionStart, ngServerTime);
    } else {
        sessionStart.value = null;
        netLoss.value = null;
    }
}

ngFrameworkLoaded().then(() => {
    useNgCasinoFunparkStore(pinia); // initialization

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ng_hs.instantGame.gameClosed((gameId: number) => {
        // do not reset game id, when we are expecting next game to launch
        if (!gameLoadPromiseResolve) {
            resetCasinoState();
            gameLoadPromiseResolve = null;
        }
    });
    ng_hs.onGamesListUpdated((gameList) => gameListReady(gameList));

    ng_hs.InstantGamesPlayingSessionUpdate((rgStatus) => {
        // Revert changes for FE-4801
        // to include net loss only for games only
        ngSessionUpdate(rgStatus.TotalNetLoss, rgStatus.PlayingSessionStartTime, rgStatus.ServerTime);
    });

    // when user toggles money mode and then closes login popup,
    // we need to forget, he wanted to change game mode
    eventBus.on(GlobalEvents.WebLoginAborted, () => {
        if (gameLoadPromiseResolve) {
            gameLoadPromiseResolve = null;
        }
    });
});

/*
    prevents user from pressing tab
    while game opened
    (header dissapears)
*/
documentReady(() => {
    document.body.addEventListener("keydown", (e) => {
        if (document.body.classList.contains("casino-open")) {
            if (e.key === "Tab") {
                e.preventDefault();
            }
        }
    });
});

/**
 * IMPORTANT: Projects outside the casino library should use `loadCasinoModule` from `use-ng-casino.ts` to asynchronously load this module
 */
export const useNgCasino = () => ({
    loadGame,
    loadTransparentGame,
    closeGame,
    ngGameReady,
    toggleMoneyMode,

    // Proxy the refs from use-ng-caisno-state
    playForMoney,
    sessionStart,
    netLoss,
    currentGameServerData,
    currentGameId,
    transparentGame,
    ngGameDefinition,
    isLiveDealerGameOpen,
});
